(function () {
  'use strict';

  // Mirrors the 5 live Qikify Volume Discounts offers. Manual sync — Qikify
  // has no stable API for this, so these must be hand-updated whenever the
  // offers change in the Qikify app (see project memory).
  var BUNDLE_TIERS = {
    ringerTee: {
      match: function (item) { return item.handle.indexOf('ringer-t-shirt') !== -1; },
      label: 'Ringer Tees',
      tiers: [
        { qty: 2, price: 849, pct: 10 },
        { qty: 3, price: 1199, pct: 20 },
        { qty: 4, price: 1299, pct: 30 },
      ],
    },
    tieDyeTee: {
      match: function (item) { return item.handle.indexOf('tie-dye-oversized-t-shirt') !== -1; },
      label: 'Tie Dye Tees',
      tiers: [
        { qty: 2, price: 1349, pct: 10 },
        { qty: 3, price: 1949, pct: 20 },
      ],
    },
    hoodieJoggerSet: {
      match: function (item) { return item.handle.indexOf('co-ord-set-hoodies-joggers') !== -1; },
      label: 'Hoodie & Jogger Sets',
      tiers: [
        { qty: 2, price: 2799, pct: 10 },
      ],
    },
    tieDyeShorts: {
      match: function (item) {
        return item.handle.indexOf('tie-dye-oversized-t-shirt') !== -1 ||
          item.handle.indexOf('tie-dye-shorts') !== -1;
      },
      label: 'Tie Dye Tee + Shorts',
      tiers: [
        { qty: 2, price: 1449, pct: 10 },
      ],
    },
    tieDyeJogger: {
      match: function (item) {
        return item.handle.indexOf('tie-dye-oversized-t-shirt') !== -1 ||
          item.handle.indexOf('cotton-jogger') !== -1;
      },
      label: 'Tie Dye Tee + Jogger',
      tiers: [
        { qty: 2, price: 1349, pct: 10 },
      ],
    },
  };

  class YlCartDrawer extends HTMLElement {
    connectedCallback() {
      this.backdrop = document.getElementById('yl-cart-backdrop');
      this.closeBtn = this.querySelector('.yl-cart-drawer__close');
      this.body = this.querySelector('.yl-cart-drawer__body');
      this.footer = this.querySelector('.yl-cart-drawer__footer');
      this.promoEl = this.querySelector('#ylCartPromo');
      this.upsellEl = this.querySelector('#ylCartUpsell');
      this.cachedCart = null;
      this._reconcileTimer = null;

      this.closeBtn && this.closeBtn.addEventListener('click', () => this.close());
      this.backdrop && this.backdrop.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      this.setupFooterControls();
      this.refresh();
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.backdrop) this.backdrop.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.backdrop) this.backdrop.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    // Qikify (and any Dawn-style app) calls this after it adds to cart.
    // If the global fetch hook below already merged that same /cart/add.js
    // response (the common case, once cachedCart exists), this just opens
    // the already-up-to-date drawer instead of paying for a second /cart.js
    // round trip. Only a true cold start (no cached cart yet) falls back to
    // a full refresh.
    renderContents() {
      if (this.cachedCart) {
        this.open();
      } else {
        this.refresh().then(() => this.open());
      }
    }

    async refresh() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.cachedCart = cart;
      this.classList.toggle('is-empty', cart.item_count === 0);
      this.renderCart(cart);
      this.renderPromo(cart);
      this.renderUpsell(cart);
      this.updateCartCount(cart.item_count);
      return cart;
    }

    // Optimistically folds item(s) just added (from a /cart/add.js response —
    // ours, Qikify's bundle widget, or Horizon's native quick-add, whichever
    // fired the request) into the last-known cart state and repaints
    // immediately, instead of every caller separately waiting on a second
    // /cart.js round trip before the drawer shows anything. A short
    // debounced /cart.js reconcile follows in the background to correct
    // anything this line-level math can't account for (e.g. cart-level
    // discount rounding) — it never blocks the drawer opening.
    mergeAddedItems(data) {
      if (!data) return false;
      const added = Array.isArray(data.items) ? data.items : (data.id ? [data] : null);
      if (!added || !added.length || !this.cachedCart) return false;

      const cart = this.cachedCart;
      added.forEach((item) => {
        const existing = cart.items.find((i) => i.key === item.key);
        if (existing) {
          existing.quantity = item.quantity;
          existing.line_price = item.line_price;
          existing.final_line_price = item.final_line_price;
        } else {
          cart.items.push(item);
        }
      });
      cart.item_count = cart.items.reduce((sum, i) => sum + i.quantity, 0);
      cart.total_price = cart.items.reduce((sum, i) => sum + i.final_line_price, 0);

      this.classList.toggle('is-empty', cart.item_count === 0);
      this.renderCart(cart);
      this.renderPromo(cart);
      this.renderUpsell(cart);
      this.updateCartCount(cart.item_count);
      this.open();

      // Coalesce rapid successive adds (e.g. a multi-unit bundle firing
      // several /cart/add.js calls) into a single authoritative refresh
      // once things settle, rather than one per add.
      clearTimeout(this._reconcileTimer);
      this._reconcileTimer = setTimeout(() => this.refresh(), 700);
      return true;
    }

    renderCart(cart) {
      if (!this.body) return;

      if (cart.item_count === 0) {
        this.body.innerHTML = '<div class="yl-cart-drawer__empty">Your bag is empty.</div>';
        if (this.footer) this.footer.style.display = 'none';
        return;
      }

      if (this.footer) this.footer.style.display = '';

      this.body.innerHTML = cart.items.map((item) => `
        <div class="yl-cart-line" data-key="${item.key}">
          <a href="${item.url}" class="yl-cart-line__img">
            <img src="${item.image}" alt="${item.product_title}" loading="lazy">
          </a>
          <div class="yl-cart-line__info">
            <a href="${item.url}" class="yl-cart-line__title">${item.product_title}</a>
            ${item.variant_title ? `<div class="yl-cart-line__variant">${item.variant_title}</div>` : ''}
            <div class="yl-cart-line__row">
              <div class="yl-cart-line__qty">
                <button type="button" class="yl-cart-line__qty-btn" data-qty-down>−</button>
                <span>${item.quantity}</span>
                <button type="button" class="yl-cart-line__qty-btn" data-qty-up>+</button>
              </div>
              <span class="yl-cart-line__price">${this.money(item.final_line_price)}</span>
            </div>
          </div>
          <button type="button" class="yl-cart-line__remove" aria-label="Remove">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
      `).join('');

      if (this.footer) {
        const subtotalEl = this.footer.querySelector('.yl-cart-drawer__subtotal-value');
        if (subtotalEl) subtotalEl.textContent = this.money(cart.total_price);
      }

      this.body.querySelectorAll('[data-qty-up]').forEach((btn) =>
        btn.addEventListener('click', (e) => this.changeQty(e, 1)));
      this.body.querySelectorAll('[data-qty-down]').forEach((btn) =>
        btn.addEventListener('click', (e) => this.changeQty(e, -1)));
      this.body.querySelectorAll('.yl-cart-line__remove').forEach((btn) =>
        btn.addEventListener('click', (e) => this.removeLine(e)));
    }

    // Real progress toward an actual live Qikify multibuy tier — not a fake
    // countdown or invented urgency, only shows when the cart genuinely
    // qualifies for a bucket and reflects real tier thresholds/prices.
    // Some products sit in more than one offer (e.g. a Tie Dye Tee counts
    // toward the standalone Tie Dye deal AND the +Shorts/+Jogger combos), so
    // every offer is scored and whichever is closest to unlocking wins.
    computeBundleProgress(cart) {
      let best = null;
      for (const key in BUNDLE_TIERS) {
        const cfg = BUNDLE_TIERS[key];
        const qty = cart.items.filter(cfg.match).reduce((sum, i) => sum + i.quantity, 0);
        if (qty === 0) continue;

        const nextTier = cfg.tiers.find((t) => t.qty > qty);
        let info;
        if (nextTier) {
          const remaining = nextTier.qty - qty;
          info = {
            text: `Add <b>${remaining} more</b> ${cfg.label} — buy ${nextTier.qty} for ₹${nextTier.price} (${nextTier.pct}% off)`,
            pct: Math.min(100, (qty / nextTier.qty) * 100),
            remaining,
          };
        } else {
          const maxTier = cfg.tiers[cfg.tiers.length - 1];
          info = {
            text: `You've unlocked the best ${cfg.label} deal — <b>${maxTier.qty} for ₹${maxTier.price}</b>`,
            pct: 100,
            remaining: 0,
          };
        }
        if (!best || info.pct > best.pct || (info.pct === best.pct && info.remaining < best.remaining)) {
          best = info;
        }
      }
      return best;
    }

    renderPromo(cart) {
      if (!this.promoEl) return;
      if (cart.item_count === 0) { this.promoEl.innerHTML = ''; return; }
      const info = this.computeBundleProgress(cart);
      if (!info) { this.promoEl.innerHTML = ''; return; }
      this.promoEl.innerHTML = `
        <div class="yl-cart-promo">
          <div class="yl-cart-promo__text">${info.text}</div>
          <div class="yl-cart-promo__bar"><div class="yl-cart-promo__fill" style="width:${info.pct}%"></div></div>
        </div>
      `;
    }

    async renderUpsell(cart) {
      if (!this.upsellEl) return;
      if (cart.item_count === 0) { this.upsellEl.innerHTML = ''; return; }
      try {
        const firstProductId = cart.items[0].product_id;
        const res = await fetch(`/recommendations/products.json?product_id=${firstProductId}&limit=4&intent=related`);
        if (!res.ok) throw new Error('recommendations request failed');
        const data = await res.json();
        const cartProductIds = cart.items.map((i) => i.product_id);
        const products = (data.products || [])
          .filter((p) => cartProductIds.indexOf(p.id) === -1)
          .slice(0, 4);
        if (!products.length) { this.upsellEl.innerHTML = ''; return; }
        this.upsellEl.innerHTML = `
          <div class="yl-cart-drawer__upsell-title">You might also like</div>
          <div class="yl-cart-drawer__upsell-row">
            ${products.map((p) => `
              <a href="${p.url}" class="yl-upsell-card">
                <img src="${p.featured_image}" alt="${p.title}" loading="lazy">
                <span class="yl-upsell-card__title">${p.title}</span>
                <span class="yl-upsell-card__price">${this.money(p.price)}</span>
              </a>
            `).join('')}
          </div>
        `;
      } catch (e) {
        this.upsellEl.innerHTML = '';
      }
    }

    setupFooterControls() {
      const applyBtn = this.querySelector('#ylDiscountApply');
      const discountInput = this.querySelector('#ylDiscountInput');
      if (applyBtn && discountInput) {
        const apply = () => {
          const code = discountInput.value.trim();
          if (!code) return;
          window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
        };
        applyBtn.addEventListener('click', apply);
        discountInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') apply(); });
      }

      const noteToggle = this.querySelector('#ylNoteToggle');
      const noteInput = this.querySelector('#ylNoteInput');
      if (noteToggle && noteInput) {
        noteToggle.addEventListener('click', () => {
          const showing = noteInput.style.display !== 'none';
          noteInput.style.display = showing ? 'none' : 'block';
          noteToggle.textContent = showing ? '+ Add order note' : '− Hide order note';
          if (!showing) noteInput.focus();
        });
        noteInput.addEventListener('blur', () => {
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: noteInput.value }),
          });
        });
      }
    }

    async changeQty(e, delta) {
      const line = e.target.closest('.yl-cart-line');
      const key = line.dataset.key;
      const currentQty = parseInt(line.querySelector('.yl-cart-line__qty span').textContent, 10);
      const newQty = Math.max(0, currentQty + delta);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty }),
      });
      await this.refresh();
    }

    async removeLine(e) {
      const line = e.target.closest('.yl-cart-line');
      const key = line.dataset.key;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      await this.refresh();
    }

    updateCartCount(count) {
      document.querySelectorAll('.ylh__cart-count').forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    }

    money(cents) {
      return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });
    }
  }

  customElements.define('cart-drawer', YlCartDrawer);

  // Globally observe every POST to /cart/add(.js) — regardless of who makes
  // it (this file's own submit handler below, Qikify's bundle-tier widget,
  // Horizon's native quick-add) — and optimistically update the drawer the
  // moment that response resolves. Previously every one of those callers
  // separately waited on the add, then waited AGAIN on a full follow-up
  // /cart.js fetch before showing anything — two blocking round trips per
  // add, or more for a multi-unit bundle. This collapses it to the one
  // round trip each caller already has to make. Uses response.clone() so
  // the original caller can still read the body itself untouched.
  var _ylOrigFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (typeof input !== 'string' && input && input.method) || 'GET').toUpperCase();
    var isCartAdd = method === 'POST' && url.indexOf('/cart/add') !== -1;
    var promise = _ylOrigFetch.apply(this, arguments);
    if (isCartAdd) {
      promise.then(function (res) {
        if (!res.ok) return;
        res.clone().json().then(function (data) {
          var drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.mergeAddedItems(data);
        }).catch(function () {});
      });
    }
    return promise;
  };

  // Intercept any /cart/add form submit on the site (covers Add to Bag + sticky bar)
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return; // no drawer on this page — let the form submit natively to /cart instead of silently doing nothing

    e.preventDefault();

    const formData = new FormData(form);
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || 'Could not add to cart');
        // The global fetch hook above has already merged this same response
        // and opened the drawer in the common case; this is a safety net
        // only for a true cold start (no cached cart yet).
        if (!drawer.cachedCart) return drawer.refresh().then(() => drawer.open());
        drawer.open();
      })
      .catch((err) => {
        console.error('[yl-cart-drawer]', err);
        alert(err.message || 'Something went wrong adding this to your bag.');
      })
      .finally(() => {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
      });
  });

  // Header cart icon opens the drawer instead of navigating to /cart
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-cart-drawer-toggle]');
    if (!trigger) return;
    e.preventDefault();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.refresh().then(() => drawer.open());
  });
})();